<template>
  <div class="pdf-collection-sidebar">
    <div class="pdf-collection-sidebar__header">
      <span>{{ t('collection.title') }}</span>
      <span class="pdf-collection-sidebar__counter">
        {{ currentIndex + 1 }} / {{ items.length }}
      </span>
    </div>

    <div class="pdf-collection-sidebar__list">
      <div
        v-for="(item, index) in sizedItems"
        :key="item.asset.uid"
        class="pdf-collection-sidebar__item"
        :class="{ 'pdf-collection-sidebar__item--active': index === currentIndex }"
        @click="emit('select', index)"
      >
        <div class="pdf-collection-sidebar__thumbnail">
          <img
            v-if="item.asset.thumbnailUrl"
            :src="item.asset.thumbnailUrl"
            :alt="item.customTitle || item.asset.name"
            class="pdf-collection-sidebar__thumbnail-img"
          />
          <v-icon
            v-else
            :icon="mdiFileDocumentOutline"
            size="32"
            class="pdf-collection-sidebar__thumbnail-placeholder"
          />
        </div>

        <div class="pdf-collection-sidebar__info">
          <span class="pdf-collection-sidebar__title">
            {{ item.customTitle || item.asset.name }}
          </span>
          <span v-if="item.asset.pageCount" class="pdf-collection-sidebar__pages">
            {{ item.asset.pageCount }} {{ item.asset.pageCount > 1 ? 'pages' : 'page' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiFileDocumentOutline } from '@mdi/js'
import { usePDFI18n } from '../i18n'

const { t } = usePDFI18n()
import type { CollectionItem } from '../../types/player'
import type { DocumentAssetListItem } from '../../types/asset'
import { withThumbnailSize } from '../../common/thumbnail'

const props = defineProps<{
  items: CollectionItem<DocumentAssetListItem>[]
  currentIndex: number
}>()

const sizedItems = computed(() =>
  props.items.map((item) => ({
    ...item,
    asset: {
      ...item.asset,
      // A 48px square tile, cropped by its own `object-fit: cover`; 160 is the rung above twice it.
      thumbnailUrl: withThumbnailSize(item.asset.thumbnailUrl, 160, 160, 'cover'),
    },
  })),
)

const emit = defineEmits<{
  select: [index: number]
}>()
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-collection-sidebar {
  width: 240px;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    padding: $space-md $space-lg;
    font-size: $text-base;
    font-weight: $font-medium;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__counter {
    font-size: $text-sm;
    font-weight: $font-normal;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: $space-sm;
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: $space-md;
    cursor: pointer;
    padding: $space-sm;
    border-radius: $radius-sm;
    border-left: 3px solid transparent;
    transition: background-color $duration-fast $ease-default, border-left-color $duration-fast $ease-default;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.05);
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.1);
      border-left-color: rgb(var(--v-theme-primary));

      .pdf-collection-sidebar__title {
        color: rgb(var(--v-theme-primary));
        font-weight: $font-medium;
      }
    }
  }

  &__thumbnail {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: $radius-sm;
    overflow: hidden;
    background: rgba(var(--v-theme-on-surface), 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__thumbnail-placeholder {
    color: rgba(var(--v-theme-on-surface), 0.3);
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-size: $text-base;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__pages {
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }

  @include reduced-motion {
    .pdf-collection-sidebar__item {
      transition: none;
    }
  }
}
</style>
